import type { ReactNode } from 'react'
import {
  Button as AriaButton,
  FieldError as AriaFieldError,
  Label as AriaLabel,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
  Text,
} from 'react-aria-components'
import {
  Check,
  ChevronDown,
} from 'lucide-react'

import type { FormFieldErrorMessage } from './form-field'
import { cn } from '@/shared/lib/cn'

export interface SelectOption {
  id: string
  label: string
  description?: string
  isDisabled?: boolean
}

export interface SelectProps {
  label: ReactNode
  options: readonly SelectOption[]

  name?: string
  value?: string | null
  defaultValue?: string

  onChange?: (
    value: string | null,
  ) => void

  placeholder?: string
  description?: ReactNode
  errorMessage?: FormFieldErrorMessage

  isRequired?: boolean
  isDisabled?: boolean
  isInvalid?: boolean

  className?: string
  triggerClassName?: string
}

export function Select({
  label,
  options,
  name,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select an option',
  description,
  errorMessage,
  isRequired = false,
  isDisabled = false,
  isInvalid,
  className,
  triggerClassName,
}: SelectProps) {
  const hasStaticError =
    typeof errorMessage === 'string' &&
    errorMessage.trim().length > 0

  const resolvedIsInvalid =
    isInvalid ?? hasStaticError

  return (
    <AriaSelect
      selectionMode="single"
      name={name}
      value={value ?? undefined}
      defaultValue={defaultValue}
      onChange={(nextValue) => {
        if (nextValue === null) {
          onChange?.(null)
          return
        }

        onChange?.(String(nextValue))
      }}
      placeholder={placeholder}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isInvalid={resolvedIsInvalid}
      validationBehavior="aria"
      className={cn(
        'flex w-full flex-col gap-1.5',
        className,
      )}
    >
      {({ isInvalid: fieldIsInvalid }) => (
        <>
          <AriaLabel
            className={[
              'flex items-center gap-1',
              'text-sm font-medium',
              'text-(--color-foreground)',
            ].join(' ')}
          >
            <span>{label}</span>

            {isRequired ? (
              <span
                aria-hidden="true"
                className="text-(--color-danger)"
              >
                *
              </span>
            ) : null}
          </AriaLabel>

          <AriaButton
            className={cn(
              [
                'flex h-10 min-h-10 w-full items-center gap-2',
                'cursor-pointer rounded-lg border px-3',
                'bg-(--color-surface)',
                'text-left text-sm',
                'text-(--color-foreground)',
                'outline-none transition-colors',
                'focus-visible:ring-2',
                'focus-visible:ring-(--color-primary)/25',
                'disabled:cursor-not-allowed',
                'disabled:opacity-60',
              ],
              fieldIsInvalid
                ? 'border-(--color-danger)'
                : [
                    'border-(--color-border)',
                    'hover:border-(--color-primary)',
                  ],
              triggerClassName,
            )}
          >
            <SelectValue
              className={[
                'min-w-0 flex-1 truncate',
                'data-placeholder:text-(--color-muted-foreground)',
              ].join(' ')}
            >
              {({
                isPlaceholder,
                selectedText,
              }) =>
                isPlaceholder
                  ? placeholder
                  : selectedText
              }
            </SelectValue>

            <ChevronDown
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-(--color-muted-foreground)"
            />
          </AriaButton>

          {description ? (
            <Text
              slot="description"
              className="text-xs leading-5 text-(--color-muted-foreground)"
            >
              {description}
            </Text>
          ) : null}

          <AriaFieldError
            className="text-xs leading-5 text-(--color-danger)"
          >
            {errorMessage}
          </AriaFieldError>

          <Popover
            placement="bottom start"
            offset={6}
            className={[
              'z-50 w-(--trigger-width)',
              'overflow-hidden rounded-lg border',
              'border-(--color-border)',
              'bg-(--color-surface)',
              'shadow-[var(--shadow-popover)] outline-none',
            ].join(' ')}
          >
            <ListBox
              className={[
                'max-h-72 overflow-y-auto',
                'p-1 outline-none',
              ].join(' ')}
            >
              {options.map((option) => (
                <ListBoxItem
                  key={option.id}
                  id={option.id}
                  textValue={option.label}
                  isDisabled={option.isDisabled}
                  className={({
                    isFocused,
                    isSelected,
                    isDisabled:
                      optionIsDisabled,
                  }) =>
                    cn(
                      [
                        'flex cursor-default items-center gap-3',
                        'rounded-md px-3 py-2',
                        'text-sm outline-none',
                      ],
                      isFocused &&
                        'bg-(--color-primary-muted)',
                      isSelected &&
                        'text-(--color-primary)',
                      optionIsDisabled
                        ? 'opacity-50'
                        : 'cursor-pointer',
                    )
                  }
                >
                  {({ isSelected }) => (
                    <>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">
                          {option.label}
                        </div>

                        {option.description ? (
                          <div className="truncate text-xs text-(--color-muted-foreground)">
                            {option.description}
                          </div>
                        ) : null}
                      </div>

                      {isSelected ? (
                        <Check
                          aria-hidden="true"
                          className="h-4 w-4 shrink-0"
                        />
                      ) : null}
                    </>
                  )}
                </ListBoxItem>
              ))}
            </ListBox>
          </Popover>
        </>
      )}
    </AriaSelect>
  )
}
